/**
 * Multi-select de checkboxes en un Popover — el filtro "varios valores a la
 * vez" que hasta ahora se resolvía con selects de un solo valor disfrazados de
 * array (DeviceTable, CommunityMapTable). Presentacional y genérico: recibe
 * opciones con conteo opcional, devuelve el array seleccionado. Nació con la
 * vista Hiring Plan de TA (21/09/2026); está en el catálogo de reutilización.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label?: string;
  /** Conteo a la derecha (por ejemplo cuántas filas tienen ese valor). */
  count?: number;
}

export interface MultiSelectPopoverProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Placeholder del buscador interno (aparece con más de 8 opciones). */
  searchPlaceholder?: string;
  /** Texto del vaciado ("Todos"). */
  allLabel?: string;
  className?: string;
  /** Ancho fijo del trigger para que la barra de filtros no baile. */
  triggerClassName?: string;
}

export function MultiSelectPopover({ label, options, selected, onChange, searchPlaceholder, allLabel, className, triggerClassName }: MultiSelectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const visibles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => (o.label ?? o.value).toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const resumen = selected.length === 0
    ? (allLabel ?? '—')
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={selected.length ? 'secondary' : 'outline'}
          size="sm"
          className={cn('h-8 justify-between gap-1.5 font-normal', triggerClassName, className)}
          aria-label={label}
        >
          <span className="truncate">
            <span className="text-muted-foreground">{label}:</span> <span className="font-medium">{resumen}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        {options.length > 8 && (
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} className="mb-2 h-8 text-xs" autoFocus />
        )}
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {visibles.map((o) => {
            const on = selected.includes(o.value);
            return (
              <label key={o.value} className={cn('flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-accent', on && 'bg-accent/60')}>
                <Checkbox checked={on} onCheckedChange={() => toggle(o.value)} aria-label={o.label ?? o.value} />
                <span className="flex-1 truncate">{o.label ?? o.value}</span>
                {typeof o.count === 'number' && <span className="text-xs tabular-nums text-muted-foreground">{o.count}</span>}
                {on && <Check className="h-3.5 w-3.5 text-primary" />}
              </label>
            );
          })}
          {visibles.length === 0 && <p className="px-1.5 py-2 text-xs text-muted-foreground">—</p>}
        </div>
        {selected.length > 0 && (
          <Button type="button" variant="ghost" size="sm" className="mt-2 h-7 w-full gap-1 text-xs" onClick={() => onChange([])}>
            <X className="h-3 w-3" /> {allLabel ?? '—'}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
